import { create } from 'zustand';
import type { User, Site, Template, ValidationResult } from '../types';

// ─── App State ───────────────────────────────────────────────────────

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;

  // Current working site (being built)
  currentHtml: string;
  setCurrentHtml: (html: string) => void;
  selectedTemplate: Template | null;
  setSelectedTemplate: (t: Template | null) => void;
  templateVariables: Record<string, string>;
  setTemplateVariable: (key: string, value: string) => void;
  setAllTemplateVariables: (vars: Record<string, string>) => void;

  // Validation
  validationResult: ValidationResult | null;
  setValidationResult: (result: ValidationResult | null) => void;

  // Sites
  mySites: Site[];
  setMySites: (sites: Site[]) => void;

  // UI
  activePage: string;
  setActivePage: (page: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  toast: { message: string; type: 'success' | 'error' | 'info' } | null;
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  hideToast: () => void;

  // Reset builder flow
  resetBuilder: () => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Auth
  user: null,
  isAuthenticated: false,
  setUser: (user) => set({ user, isAuthenticated: !!user }),

  // Builder
  currentHtml: '',
  setCurrentHtml: (html) => set({ currentHtml: html }),
  selectedTemplate: null,
  setSelectedTemplate: (t) => set({ selectedTemplate: t }),
  templateVariables: {},
  setTemplateVariable: (key, value) =>
    set((state) => ({ templateVariables: { ...state.templateVariables, [key]: value } })),
  setAllTemplateVariables: (vars) => set({ templateVariables: vars }),

  // Validation
  validationResult: null,
  setValidationResult: (result) => set({ validationResult: result }),

  // Sites
  mySites: [],
  setMySites: (sites) => set({ mySites: sites }),

  // UI
  activePage: '/',
  setActivePage: (page) => set({ activePage: page }),
  isLoading: false,
  setIsLoading: (loading) => set({ isLoading: loading }),
  toast: null,
  showToast: (message, type = 'info') => {
    set({ toast: { message, type } });
    setTimeout(() => set({ toast: null }), 4000);
  },
  hideToast: () => set({ toast: null }),

  resetBuilder: () =>
    set({
      currentHtml: '',
      selectedTemplate: null,
      templateVariables: {},
      validationResult: null,
    }),
}));
